import { useEffect } from 'react';

/**
 * usePolling — run `fn` on mount and every `intervalMs` while the tab
 * is visible. Automatically pauses when the browser tab is hidden and
 * resumes (with an immediate refresh) when it comes back to the fore.
 *
 * Why this exists: the admin app runs multiple background pollers
 * (notifications, staff activity, treasury float, health data, etc.),
 * each hitting Vercel edge on every tick regardless of whether anyone
 * was looking. On admins who leave the console open 24/7, that adds up
 * to millions of edge requests a month. Gating on document.visibility
 * cuts that cost without changing UX — when you switch away, we stop;
 * when you come back, you get fresh data immediately.
 *
 * If you truly need to keep polling while hidden (e.g. a critical
 * background sync), don't use this hook — use `setInterval` directly.
 *
 * @param {Function} fn — the poll callback
 * @param {number}   intervalMs — poll cadence in ms
 * @param {Array}    deps — dependency array (like useEffect); polling
 *                          restarts when these change
 * @param {Object}   [options]
 * @param {boolean}  [options.enabled=true] — set false to disable polling
 */
export function usePolling(fn, intervalMs, deps = [], options = {}) {
  const { enabled = true } = options;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!enabled || !intervalMs || intervalMs <= 0) return;

    let interval = null;
    const start = () => {
      if (interval) return;
      interval = setInterval(fn, intervalMs);
    };
    const stop = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };

    // Initial call — always, so consumers get data immediately on mount.
    fn();
    if (!document.hidden) start();

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        fn();   // fresh data on return
        start();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [intervalMs, enabled, ...deps]);
}

export default usePolling;
